"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDebouncedState = void 0;
var react_1 = require("react");
var lodash_1 = require("lodash");
var DEFAULT_INTERVAL = 250;
/**
  Use this hook when sub-second state updates are expected but notifying parent components
  would be too expensive at that frequency.

  This hook's local state and any direct consumers of the state are updated on every change;
  however, the onChange handler will be notified at most once per interval.

  Arguments
  * startValue: initial state
  * onChange: the callback we should call with state with changes
  * interval: debounce interval in milliseconds

  Returns [value, setValue, flush]
  * value: the state value
  * setValue: the state setter function
  * flush: the debounce flush function

  Example usage
  ```
  import { useDebouncedState } from '/util/debounced_state'

  export const DebouncedInputExample: React.FunctionComponent<{value: string, onChange: (value: string) => void}> = (props): JSX.Element => {
    const [value, setValue] = useDebouncedState(props.value, props.onChange)
    return <input type="text" value={value} onChange={HandleEventChange((newValue: string | undefined) => setValue(newValue))}></input>
  }
  ```
*/
function useDebouncedState(startValue, onChange, interval) {
    if (interval === void 0) { interval = DEFAULT_INTERVAL; }
    var _a = (0, react_1.useState)(startValue), value = _a[0], setValue = _a[1];
    // we need to useRef here because we should only have (1) debounced func no matter how many times the provided onChange handler changes
    var onChangeDebounced = (0, react_1.useRef)((0, lodash_1.debounce)(function (setStateAction, onChange) {
        if (typeof setStateAction === 'function') {
            onChange(setStateAction(value));
        }
        else {
            onChange(setStateAction);
        }
    }, interval));
    var wrappedSetValue = function (setStateAction) {
        setValue(setStateAction);
        if (onChangeDebounced.current) {
            onChangeDebounced.current(setStateAction, onChange);
        }
    };
    return [value, wrappedSetValue, function () { return onChangeDebounced.current.flush(); }];
}
exports.useDebouncedState = useDebouncedState;
